import { difference, isEqual } from 'underscore';
import { IMAGE_FILE } from '@shoflo/polymer/constants/SourceItems';
import { applyAnimations } from '@studio/program/animations/applyAnimations';
import { sanitizeScene as sanitize } from '@studio/program/utils/scene';
import { sanitizeProgramForLog } from '@studio/logger/utils/sanitizeProgramForLog';

import updateOverlayPosition from 'actions/Broadcast/overlays/updateOverlayPosition';
import { BROADCAST_SET_PROGRAM } from 'actions/Types';
import { addSignedUrlsToBroadcast, withCurrentParams } from 'actions/utils';
import setSelectedScene from 'actions/Broadcast/scene/setSelectedScene';
import takeSceneLive from 'actions/Broadcast/scene/takeSceneLive';
import syncBroadcastSources from '@shoflo/studio-redux/actions/program/syncBroadcastSources';
import handleAgoraOnStagePriority from '@shoflo/studio-redux/actions/meeting/handleAgoraOnStagePriority';
import agoraOnstageAttendeeUpdater from '@cvent/studio-agora/metrics/agoraOnstageAttendeeUpdater';
import { StudioLogger } from '@studio/logger/StudioLogger';

const logger = new StudioLogger('app.studio.director.setProgram');

/**
 * @param scene object the scene to set the program to
 * @param isTransient boolean whether we should save this to the API or not, if isTransient is true, persistence is not desired.
 * @param otherConfig Object containing other configs
 */
export default function setProgram(
  scene,
  isTransient = false,
  otherConfig = { emitSocket: true },
  endActionCallback = null
) {
  return withCurrentParams(
    (dispatch, { teamId, eventId, broadcastId }, getState) => {
      const {
        program,
        broadcastScenes: { sceneTakenToLive },
        overlays,
        backgrounds,
        sceneLayers
      } = getState();

      const { isFromScene, updatedSceneLayers } = otherConfig;
      /**
       * There is an instance where there is no change to a scene,
       * we shouldn't update the program because it causes undesired change to broadcast sources
       */
      if (isEqual(scene, program)) return;

      const action = {
        type: BROADCAST_SET_PROGRAM,
        payload: applyAnimations(program, scene),
        sceneLayerSources: { overlays, backgrounds },
        request: async apiClient => {
          try {
            //Deep clone, because this appears to mutate the parameter
            const scenePayload = sanitize(JSON.parse(JSON.stringify(scene)));
            let broadcast = await apiClient.broadcast.updateProgram(
              teamId,
              eventId,
              broadcastId,
              scenePayload
            );

            /**
             * API only returns ` overlays, backgrounds, globalSceneLayers`
             * only when we update program from scene.
             * If these parameters are not returned, we get them from state.
             */
            if (!broadcast.overlays) {
              broadcast = {
                ...broadcast,
                overlays
              };
            }

            if (!broadcast.backgrounds) {
              broadcast = {
                ...broadcast,
                backgrounds
              };
            }

            if (!broadcast.globalSceneLayers) {
              broadcast.globalSceneLayers = updatedSceneLayers ?? sceneLayers;
            }

            const result = await addSignedUrlsToBroadcast(
              { ...broadcast, team: teamId, event: eventId },
              apiClient,
              teamId,
              eventId
            );
            if (typeof endActionCallback === 'function') {
              endActionCallback();
            }
            return result;
          } catch (error) {
            if (typeof endActionCallback === 'function') {
              endActionCallback({ error: error.message });
            }
            throw error;
          }
        },
        isTransient,
        otherConfig
      };
      // If it's transient, don't save to API...
      if (isTransient || action.isTransient) delete action.request;

      // if scene is live and there's a change in program, we want to remove the scene from
      // live and select it. Skip this when the program change is from an intentional scene
      // switch (isFromScene), as it can incorrectly revert to the previous scene.
      if (!isFromScene && sceneTakenToLive && program.layers) {
        const programDiff = difference(program.layers, sceneTakenToLive.layers);
        if (programDiff.length) {
          dispatch(takeSceneLive(null, true));
          dispatch(setSelectedScene(sceneTakenToLive));
        }
      }

      // Get the image layers in the scene (We just want the overlays really)
      const imageLayers = scene.layers.filter(
        item => item?.source?.type === IMAGE_FILE
      );

      // we want to update the actual position of the overlay if the value
      // in the scene is different from the overlay value
      if (imageLayers.length) {
        for (const imageLayer of imageLayers) {
          const overlay = overlays.find(item => item._id === imageLayer._id);

          if (
            overlay &&
            overlay.layer.title &&
            overlay.layer.title !== imageLayer.layer.title
          ) {
            dispatch(
              updateOverlayPosition(overlay._id, imageLayer.layer.title)
            );
          }
        }
      }

      if (isFromScene) {
        dispatch(syncBroadcastSources(scene));
      }
      const { meeting } = getState();
      const attendeeId = meeting?.attendeeResponse?.Attendee?.AttendeeId;
      const sceneLog = sanitizeProgramForLog(scene);
      logger.info('setProgram - sent', {
        broadcastId,
        attendeeId,
        scene: sceneLog,
        isTransient,
        isFromScene,
        willEmitSocket: !isTransient && otherConfig.emitSocket !== false
      });
      agoraOnstageAttendeeUpdater(scene, meeting);
      dispatch(handleAgoraOnStagePriority({ payload: scene }));
      dispatch(action);
    }
  );
}
